import React, { useState } from 'react';
import { Send, FilePlus2, Trash2, Plus, Users, Mail, Check, Eye } from 'lucide-react';
import { Button, Modal, ReferenceModal, DocumentViewer } from '../../../components';
import recruitmentService from '../../../services/carehome_manager/recruitmentService';
import {
  cardCls, inputCls, fieldLabelCls, Pill, Section, fmtDate, fmtDateTime, fileHref, linkStatus,
} from './shared';
import LinkModal from './LinkModal';
import ManagerChecksSection from './ManagerChecksSection';
import HistorySection from './HistorySection';

/**
 * Step 3 — Personal details.
 *
 * The second and last thing a candidate is sent, after an interview has gone
 * well: name and account, personal identity, address, emergency contact and
 * bank details.
 *
 * Two absences here are deliberate, and both are the point of having two forms:
 *
 *   NO COMPLIANCE SECTION. Right to Work and DBS were collected and decided at
 *   step 1, before the interview. Repeating them here would mean two screens
 *   able to record the same verdict, and a candidate being asked to type the
 *   numbers off documents the organisation already holds.
 *
 *   NO STANDING DOCUMENT LIST. The four pre-interview uploads cover everybody.
 *   What is left is the one-off — a particular certificate, a registration —
 *   and for that the manager REQUESTS a document by name, from the one
 *   candidate who needs to send it. Nothing is requested by default.
 *
 * References live here too, because this is the form the candidate names their
 * referees on. Each referee gets their own Send button rather than one "request
 * references" action: they are not interchangeable, and a manager is usually
 * chasing the one that has not come back.
 */

/** How each section of the returned form reads, given the state of the link. */
const sectionState = (section, link) => {
  if (!link.status || link.status === 'Not sent') return { tone: 'neutral', label: 'Not sent' };
  if (link.complete) {
    return section.complete
      ? { tone: 'good', label: 'Complete' }
      : { tone: 'warn', label: `Incomplete${section.missing ? ` · ${section.missing} outstanding` : ''}` };
  }
  // The link is out but nothing has been submitted, so anything present is a
  // draft the candidate can still change — never report it as complete.
  return section.started
    ? { tone: 'info', label: 'Draft answers saved' }
    : { tone: 'neutral', label: 'No answers yet' };
};

const PersonalDetailsPanel = ({ candidate, definitions, busy, locked, run, onRefreshList, onViewAnswers }) => {
  const [linkResult, setLinkResult] = useState(null);
  // The referee whose reference is open, if any. Held by id rather than by row
  // so a refresh after "send again" re-reads the fresh copy underneath.
  const [openReferenceId, setOpenReferenceId] = useState(null);
  // Requested documents open in place, like every other file in the app.
  const [preview, setPreview] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [requestForm, setRequestForm] = useState({ label: '', note: '', url: '' });

  const sections = candidate.sections || [];
  const extras = candidate.extra_documents || [];
  const references = candidate.references || [];

  const link = {
    status: candidate.link_status,
    sent_at: candidate.link_sent_at,
    submitted_at: candidate.submitted_at,
    complete: candidate.link_status === 'Submitted',
  };
  const status = linkStatus(link);

  const sendLink = async () => {
    const response = await run(() => recruitmentService.sendFormLink(candidate.id));
    if (!response?.data) return;
    setLinkResult(response.data);
    onRefreshList?.();
  };

  const requestDocument = async () => {
    const response = await run(() => recruitmentService.requestExtraDocument(candidate.id, requestForm));
    if (!response) return;
    setRequesting(false);
    setRequestForm({ label: '', note: '', url: '' });
  };

  const requestReference = async (row) => {
    await run(() => recruitmentService.requestReference(candidate.id, row.id));
  };

  const withdrawRequest = async (row) => {
    const warning = row.received
      ? `Remove "${row.label}"? The file the candidate sent is deleted with it.`
      : `Stop asking for "${row.label}"?`;
    if (!window.confirm(warning)) return;
    await run(() => recruitmentService.removeExtraDocument(candidate.id, row.id));
  };

  return (
    <>
      {/* ── The link ────────────────────────────────────────────────────── */}
      <div className={`${cardCls} p-5`}>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h3 className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">Personal-details form</h3>
          <Pill tone={status.tone}>{status.label}</Pill>
        </div>
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mb-4">
          Name &amp; account, personal identity, address, emergency contact and bank details.
          Existing answers are pre-filled.
        </p>

        <Button
          variant="primary" leftIcon={<Send className="w-4 h-4" />} loading={busy} disabled={locked}
          onClick={sendLink}
        >
          {candidate.link_status === 'Not sent' ? 'Send form link' : 'Open / copy form link'}
        </Button>
      </div>

      {/* ── What came back, part by part ────────────────────────────────── */}
      <div className={`${cardCls} overflow-hidden`}>
        <div className="p-5 pb-3">
          <h3 className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">Candidate details</h3>
        </div>

        {/* Each row opens what the candidate actually typed. The rows carry no
            button of their own — the whole row is the target — because the list
            reads as a status summary and a column of "View" links would turn it
            into a menu. */}
        {sections.map((section) => {
          const state = sectionState(section, link);
          return (
            <button
              key={section.id}
              type="button"
              onClick={onViewAnswers}
              title="See what the candidate filled in"
              className="w-full flex flex-wrap items-center justify-between gap-3 px-5 py-4 text-left border-t border-[#F1F5F9] dark:border-gray-800 hover:bg-[#F8FAFC] dark:hover:bg-[#2c323b] transition-colors"
            >
              <span className="text-[13.5px] text-[#0B1220] dark:text-gray-100">{section.label}</span>
              <Pill tone={state.tone}>{state.label}</Pill>
            </button>
          );
        })}
      </div>

      {/* ── Referees ────────────────────────────────────────────────────── */}
      <Section
        icon={Users}
        title="References"
        count={references.length}
        subtitle="Named by the candidate on their form. Each referee is emailed their own short form, and sees nothing else about the application."
        defaultOpen
      >
        {references.length === 0 ? (
          <p className="text-[13px] text-[#94A3B8] dark:text-gray-500">
            No referees yet — the candidate names them on the personal details form.
          </p>
        ) : (
          <div className="space-y-3">
            {references.map((row) => (
              <div key={row.id} className="p-4 rounded-xl border border-[#E3E9F3] dark:border-gray-700">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold text-[#0B1220] dark:text-gray-100">
                      {row.name || 'Name not given'}
                      <span className="ml-2 text-[12px] font-medium text-[#64748B] dark:text-gray-400">
                        {row.type === 'character' ? 'Character' : 'Employment'}
                      </span>
                    </div>
                    <div className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-0.5 break-all">
                      {row.email || 'No email address given'}
                    </div>
                    {row.relationship && (
                      <div className="text-[12.5px] text-[#475569] dark:text-gray-300 mt-0.5">{row.relationship}</div>
                    )}
                    <div className="text-[12px] mt-1.5">
                      {row.received ? (
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                          Received{row.received_at ? ` · ${fmtDateTime(row.received_at)}` : ''}
                        </span>
                      ) : row.status === 'Requested' ? (
                        <span className="text-amber-600 dark:text-amber-400 font-semibold">
                          Requested{row.requested_at ? ` · ${fmtDateTime(row.requested_at)}` : ''}
                          {row.requested_by ? ` by ${row.requested_by}` : ''}
                        </span>
                      ) : (
                        <span className="text-[#94A3B8] dark:text-gray-500">Not requested</span>
                      )}
                      {/* The candidate's tickbox is the only authority to email
                          this person, so say plainly when it is absent. */}
                      {!row.permission && !row.received && (
                        <span className="text-rose-600 dark:text-rose-400 font-semibold"> · no permission to contact</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {row.received && <Pill tone="good"><Check className="w-3 h-3 mr-1" /> Reference in</Pill>}

                    {/* Always openable. Before the answers are in it shows who
                        this referee is and where the request has got to, which
                        is what a manager chasing one actually wants to see. */}
                    <Button
                      variant="secondary" size="sm"
                      leftIcon={<Eye className="w-4 h-4" />}
                      title="View this referee and their answers"
                      onClick={() => setOpenReferenceId(row.id)}
                    >
                      View
                    </Button>

                    {!row.received && (
                    <Button
                      variant="secondary" size="sm" loading={busy}
                      leftIcon={<Mail className="w-4 h-4" />}
                      disabled={locked || !row.permission || !row.email}
                      title={!row.permission
                        ? 'The candidate has not given permission to contact this referee'
                        : !row.email
                          ? 'No email address for this referee'
                          : 'Email this referee their reference form'}
                      onClick={() => requestReference(row)}
                    >
                      {row.status === 'Requested' ? 'Send again' : 'Send form'}
                    </Button>
                    )}
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── The escape hatch: one more thing, from this candidate only ──── */}
      <Section
        icon={FilePlus2}
        title="Extra documents — only if needed"
        defaultOpen
      >
        {extras.length === 0 ? (
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-4">
            No extra documents are requested by default.
          </p>
        ) : (
          <div className="space-y-3 mb-4">
            {extras.map((row) => (
              <div
                key={row.id}
                className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl border border-[#E3E9F3] dark:border-gray-700"
              >
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold text-[#0B1220] dark:text-gray-100">{row.label}</div>
                  {row.note && (
                    <div className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-0.5">{row.note}</div>
                  )}
                  {row.url && (
                    <a
                      href={row.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[12.5px] text-blue-600 hover:underline break-all mt-0.5 inline-block"
                    >
                      {row.url}
                    </a>
                  )}
                  <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-0.5">
                    Requested {fmtDateTime(row.requested_at)}
                    {row.requested_by ? ` by ${row.requested_by}` : ''}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {row.received ? (
                    <button
                      type="button"
                      onClick={() => setPreview({
                        url: fileHref(row.file_url),
                        name: row.file_name,
                        title: row.label,
                      })}
                      className="inline-block px-3 py-1.5 rounded-lg border border-[#CBD9EB] dark:border-gray-700 text-[12.5px] font-semibold text-[#0B3F8F] dark:text-blue-300 hover:bg-[#F1F6FD] dark:hover:bg-gray-800 max-w-[240px] truncate"
                      title={`Open ${row.file_name}`}
                    >
                      {row.file_name}
                    </button>
                  ) : (
                    <Pill tone="warn">Awaiting</Pill>
                  )}
                  {!locked && (
                    <button
                      type="button"
                      onClick={() => withdrawRequest(row)}
                      className="p-1.5 text-[#94A3B8] hover:text-rose-500 transition-colors"
                      aria-label={`Stop asking for ${row.label}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!locked && (
          <Button
            variant="secondary" size="sm" leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setRequesting(true)}
          >
            Request a document
          </Button>
        )}
      </Section>

      <ManagerChecksSection
        candidate={candidate}
        definitions={definitions}
        busy={busy}
        locked={locked}
        run={run}
      />

      <HistorySection candidate={candidate} />

      <LinkModal
        result={linkResult}
        onClose={() => setLinkResult(null)}
        candidate={candidate}
        title="Candidate request link"
      />

      <DocumentViewer file={preview} onClose={() => setPreview(null)} />

      {/* The whole reference, as the referee wrote it. Shared with the staff
          record so a manager reads the same thing during recruitment and two
          years later. */}
      <ReferenceModal
        isOpen={Boolean(openReferenceId)}
        onClose={() => setOpenReferenceId(null)}
        reference={references.find((row) => row.id === openReferenceId) || null}
        fmtDate={fmtDateTime}
      />

      {/* ── Request a document ──────────────────────────────────────────── */}
      <Modal
        isOpen={requesting}
        onClose={() => setRequesting(false)}
        title="Request a document"
        maxWidth="max-w-lg"
        footer={(
          <div className="flex justify-end gap-2.5">
            <Button variant="secondary" onClick={() => setRequesting(false)}>Cancel</Button>
            <Button
              variant="primary" loading={busy} disabled={!requestForm.label.trim()}
              onClick={requestDocument}
            >
              Request it
            </Button>
          </div>
        )}
      >
        <div className="space-y-4">
          <p className="text-[13px] text-[#475569] dark:text-gray-300">
            This adds a slot to {candidate.name}&apos;s personal details form and emails them a link
            straight to it. They cannot submit the form without filling it, so ask only for
            something you actually need.
          </p>
          {candidate.link_status === 'Submitted' && (
            <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/40">
              <p className="text-[12.5px] text-[#475569] dark:text-gray-300">
                They have already submitted this form, so asking for a document reopens it. Their
                existing answers are untouched — only the lock lifts.
              </p>
            </div>
          )}

          <div>
            <label className={fieldLabelCls} htmlFor="request-label">What do you need?</label>
            <input
              id="request-label"
              className={inputCls}
              value={requestForm.label}
              placeholder="e.g. NMC registration certificate"
              onChange={(event) => setRequestForm((form) => ({ ...form, label: event.target.value }))}
            />
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
              Name it the way the candidate would recognise it — this is the label they see.
            </p>
          </div>

          <div>
            <label className={fieldLabelCls} htmlFor="request-note">Note for the candidate (optional)</label>
            <textarea
              id="request-note"
              rows={3}
              className={inputCls}
              value={requestForm.note}
              placeholder="Anything that helps them send the right thing."
              onChange={(event) => setRequestForm((form) => ({ ...form, note: event.target.value }))}
            />
          </div>

          <div>
            <label className={fieldLabelCls} htmlFor="request-url">Where to get it (optional)</label>
            <input
              id="request-url"
              type="url"
              className={inputCls}
              value={requestForm.url}
              placeholder="https://…"
              onChange={(event) => setRequestForm((form) => ({ ...form, url: event.target.value }))}
            />
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
              An application portal or a form to download. It becomes a link in their email and on
              their form. Must start with https://.
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PersonalDetailsPanel;
